import { useState } from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { formatRelativeTime } from "@/shared/lib/formatRelativeTime";
import { toastError } from "@/shared/lib/toast";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";
import { listAdminReviewQueueApi, type AdminQueueItem } from "@/portals/admin/api/adminReviewQueueApi";
import { useAdminSocketEvent } from "@/lib/adminSocket";
import { useCachedQuery } from "@/shared/hooks/useCachedQuery";
import { adminQueryKeys } from "@/portals/admin/api/adminQueryKeys";
import { ASSIGN_MESSAGES, AssignClinicianModal } from "./AssignClinicianModal";
import { MedicineNameCell } from "@/features/clinician/review-queue/components/reviewBadges";

export const MESSAGES = {
  empty: "No items waiting for a clinician.",
  ...ASSIGN_MESSAGES,
};

/** onViewItem: open a queued item read-only, before assigning it. */
export function ReviewQueueView({ onViewItem }: { onViewItem?: (id: string) => void } = {}) {
  const [target, setTarget] = useState<string | null>(null);

  // Cached: coming back to this page shows the last list at once, then refreshes it quietly.
  const queue = useCachedQuery(
    adminQueryKeys.reviewQueue,
    async () => {
      const { data, error } = await listAdminReviewQueueApi();
      return { data: data?.items ?? null, error };
    },
    { onError: (error) => toastError(error?.message ?? "Unable to load the review queue.") },
  );
  const items = queue.data ?? [];
  const load = queue.refresh;

  // Live: the server says the queue changed → refetch the list (the layout refetches the summary).
  useAdminSocketEvent("review_queue:changed", () => void load());

  const columns: ColumnsType<AdminQueueItem> = [
    { title: "Medicine", key: "medicineName", render: (_, i) => <MedicineNameCell name={i.medicineName} /> },
    { title: "Issue", key: "issue", render: (_, i) => <span className="font-semibold text-[#0A2947]">{ISSUE_LABELS[i.triggerType]}</span> },
    { title: "Patient", dataIndex: "patientDisplayName", key: "patient" },
    {
      title: "Waiting since",
      key: "waitingSince",
      render: (_, i) => (
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          {formatRelativeTime(i.waitingSince)}
          {i.overdue && (
            <span className="rounded-full bg-[#FDECEF] px-2 py-0.5 text-[10px] font-bold text-[#E4002B]">Overdue</span>
          )}
        </span>
      ),
    },
    {
      title: "",
      key: "assign",
      align: "right",
      // Pinned, so View/Assign stay in view when a narrow screen scrolls the table.
      fixed: "right",
      render: (_, i) => (
        <div className="flex items-center justify-end gap-2">
          {onViewItem && (
            <Button
              variant="outline"
              onClick={() => onViewItem(i.id)}
              className="!h-8 !rounded-lg !border-[#E5E7EB] !px-4 !text-xs !font-semibold !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]"
            >
              View
            </Button>
          )}
          <Button onClick={() => setTarget(i.id)} className="!h-8 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !text-xs !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white">
            Assign
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="rounded-3xl border border-[#E5E7EB] bg-white p-3 shadow-sm sm:p-5">
        <Table<AdminQueueItem>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={queue.loading}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: <EmptyState variant="compact" title={MESSAGES.empty} /> }}
          scroll={{ x: 760 }}
        />
      </div>

      <AssignClinicianModal
        itemId={target}
        onClose={() => setTarget(null)}
        onAssigned={(id) => {
          queue.mutate((prev = []) => prev.filter((i) => i.id !== id));
          setTarget(null);
        }}
        onItemGone={() => {
          setTarget(null);
          void load();
        }}
      />
    </div>
  );
}
